/**
 * API functions for user management (admin only).
 * Uses profile Edge Function for list and user details.
 */
import { supabase } from '../supabase';
import { normalizeSupabaseError } from './errors';
import type { AdminRole, AdminUser, AttendeeProfileWithDetails } from '../../types/database';

export interface UserListItem {
  id: string;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  admin_role: AdminRole | null;
  has_profile: boolean;
  profile_completed: boolean;
  created_at: string;
  last_sign_in_at: string | null;
  application_count: number;
}

export interface UserDetails extends UserListItem {
  profile: AttendeeProfileWithDetails | null;
}

/**
 * Get all users with their admin status and profile info.
 * Requires admin access; uses profile Edge Function.
 */
export async function getAllUsers(): Promise<UserListItem[]> {
  const { data, error } = await supabase.functions.invoke('profile', {
    method: 'POST',
    body: { list: true },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  const list = data?.data;
  if (!Array.isArray(list)) return [];
  return list as UserListItem[];
}

/**
 * Get detailed information about a specific user.
 * Requires admin access; uses profile Edge Function.
 */
export async function getUserDetails(userId: string): Promise<UserDetails | null> {
  const { data, error } = await supabase.functions.invoke('profile', {
    method: 'POST',
    body: { user_id: userId },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  return (data?.data ?? null) as UserDetails | null;
}

/**
 * Promote a user to admin
 * Requires super_admin access
 */
export async function promoteToAdmin(
  userId: string,
  role: AdminRole = 'reviewer',
  fullName?: string,
  email?: string
): Promise<AdminUser> {
  // Email is required - caller must provide it since profile fields are encrypted
  if (!email) {
    throw new Error('Email is required to promote user to admin');
  }

  const { data, error } = await supabase
    .from('admin_users')
    .insert({
      id: userId,
      role,
      full_name: fullName || null,
      email,
    })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('User is already an admin');
    }
    throw normalizeSupabaseError(error);
  }

  // Log the admin action for audit trail
  const { data: sessionData } = await supabase.auth.getSession();
  if (sessionData?.session?.user?.id) {
    await supabase.rpc('log_admin_action', {
      p_admin_user_id: sessionData.session.user.id,
      p_action: 'promote_admin',
      p_target_type: 'user',
      p_target_id: userId,
      p_details: { new_role: role, email, full_name: fullName || null },
    });
  }

  return data;
}

/**
 * Update an admin user's role
 * Requires super_admin access
 */
export async function updateAdminRole(
  userId: string,
  role: AdminRole
): Promise<AdminUser> {
  // Get previous role for audit log
  const { data: previousAdmin } = await supabase
    .from('admin_users')
    .select('role')
    .eq('id', userId)
    .maybeSingle();

  const { data, error } = await supabase
    .from('admin_users')
    .update({ role, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) throw new Error('Not found');

  // Log the admin action for audit trail
  const { data: sessionData } = await supabase.auth.getSession();
  if (sessionData?.session?.user?.id) {
    await supabase.rpc('log_admin_action', {
      p_admin_user_id: sessionData.session.user.id,
      p_action: 'update_admin_role',
      p_target_type: 'user',
      p_target_id: userId,
      p_details: { previous_role: previousAdmin?.role || null, new_role: role },
    });
  }

  return data;
}

/**
 * Remove admin privileges from a user
 * Requires super_admin access
 */
export async function demoteFromAdmin(userId: string): Promise<void> {
  // Get the admin user info before deleting for audit log
  const { data: adminUser } = await supabase
    .from('admin_users')
    .select('role, email, full_name')
    .eq('id', userId)
    .maybeSingle();

  const { error } = await supabase
    .from('admin_users')
    .delete()
    .eq('id', userId);

  if (error) throw normalizeSupabaseError(error);

  // Log the admin action for audit trail
  const { data: sessionData } = await supabase.auth.getSession();
  if (sessionData?.session?.user?.id) {
    await supabase.rpc('log_admin_action', {
      p_admin_user_id: sessionData.session.user.id,
      p_action: 'demote_admin',
      p_target_type: 'user',
      p_target_id: userId,
      p_details: adminUser ? { previous_role: adminUser.role, email: adminUser.email } : null,
    });
  }
}

/**
 * Get application summary for a user
 */
export async function getUserApplications(userId: string) {
  // First get the profile ID (maybeSingle: no profile yet is ok)
  const { data: profile, error: profileError } = await supabase
    .from('attendee_profiles')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (profileError || !profile) {
    return [];
  }

  const { data, error } = await supabase
    .from('event_applications')
    .select(`
      id,
      status,
      submitted_at,
      created_at,
      events(
        id,
        title,
        slug,
        start_date,
        end_date
      )
    `)
    .eq('attendee_profile_id', profile.id)
    .order('created_at', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}
